/**
 * UI-chrome i18n (docs/TRANSLATION_SPEC.md §13).
 *
 * Content translation is server-side and per-item (§4); this module is the
 * other half — the chrome a family reads before any content loads. Catalogs
 * are bundled statically (no network loading, no flash of untranslated keys),
 * one JSON namespace per surface under `locales/{lng}/{ns}.json`.
 *
 * Language resolution, highest priority first:
 *   1. `profile.preferred_language` — applied by `<LanguageSync />` (mounted
 *      once in `App.tsx`) as soon as the signed-in profile arrives.
 *   2. `localStorage['athleto.lang']` — what the switcher wrote while signed
 *      out, and the offline memory of a signed-in choice.
 *   3. The browser (`navigator.language`).
 *   4. `en`.
 *
 * Adding a language: drop a `locales/<tag>/` directory with the same files,
 * register it in `resources` and `UI_LANGUAGES`, and `tests/unit/
 * i18nCatalog.test.ts` will hold it to the English catalog's key set.
 */
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import enAuth from './locales/en/auth.json';
import enCommon from './locales/en/common.json';
import enFamily from './locales/en/family.json';
import enFilm from './locales/en/film.json';
import enNav from './locales/en/nav.json';
import enNotifications from './locales/en/notifications.json';
import enTeam from './locales/en/team.json';
import enCalendar from './locales/en/calendar.json';
import enRoster from './locales/en/roster.json';
import enPosts from './locales/en/posts.json';
import enMessaging from './locales/en/messaging.json';
import enLineup from './locales/en/lineup.json';
import enPlaybook from './locales/en/playbook.json';
import enConsole from './locales/en/console.json';
import enBrief from './locales/en/brief.json';
import enDistrict from './locales/en/district.json';
import enEditors from './locales/en/editors.json';
import enChallenges from './locales/en/challenges.json';
import esAuth from './locales/es/auth.json';
import esCommon from './locales/es/common.json';
import esFamily from './locales/es/family.json';
import esFilm from './locales/es/film.json';
import esNav from './locales/es/nav.json';
import esNotifications from './locales/es/notifications.json';
import esTeam from './locales/es/team.json';
import esCalendar from './locales/es/calendar.json';
import esRoster from './locales/es/roster.json';
import esPosts from './locales/es/posts.json';
import esMessaging from './locales/es/messaging.json';
import esLineup from './locales/es/lineup.json';
import esPlaybook from './locales/es/playbook.json';
import esConsole from './locales/es/console.json';
import esBrief from './locales/es/brief.json';
import esDistrict from './locales/es/district.json';
import esEditors from './locales/es/editors.json';
import esChallenges from './locales/es/challenges.json';

/** The languages the chrome ships. Phase 1: English and Spanish. */
export const UI_LANGUAGES = ['en', 'es'] as const;
export type UiLanguage = (typeof UI_LANGUAGES)[number];

export const DEFAULT_UI_LANGUAGE: UiLanguage = 'en';

/** Where a signed-out choice lives (and the offline echo of a signed-in one). */
export const LANGUAGE_STORAGE_KEY = 'athleto.lang';

/**
 * One namespace per surface; `common` is the default and the fallback. Phase 1
 * filled the first seven; the rest are phase 2's (NEXT_QUEUE_SPEC §1.2) and
 * are registered up front so each batch only ever edits its own catalogs.
 */
export const NAMESPACES = [
  'common',
  'nav',
  'auth',
  'family',
  'team',
  'film',
  'notifications',
  'calendar',
  'roster',
  'posts',
  'messaging',
  'lineup',
  'playbook',
  'console',
  'brief',
  'district',
  'editors',
  'challenges',
] as const;

export const resources = {
  en: {
    common: enCommon,
    nav: enNav,
    auth: enAuth,
    family: enFamily,
    team: enTeam,
    film: enFilm,
    notifications: enNotifications,
    calendar: enCalendar,
    roster: enRoster,
    posts: enPosts,
    messaging: enMessaging,
    lineup: enLineup,
    playbook: enPlaybook,
    console: enConsole,
    brief: enBrief,
    district: enDistrict,
    editors: enEditors,
    challenges: enChallenges,
  },
  es: {
    common: esCommon,
    nav: esNav,
    auth: esAuth,
    family: esFamily,
    team: esTeam,
    film: esFilm,
    notifications: esNotifications,
    calendar: esCalendar,
    roster: esRoster,
    posts: esPosts,
    messaging: esMessaging,
    lineup: esLineup,
    playbook: esPlaybook,
    console: esConsole,
    brief: esBrief,
    district: esDistrict,
    editors: esEditors,
    challenges: esChallenges,
  },
} as const;

export const isUiLanguage = (tag: unknown): tag is UiLanguage =>
  typeof tag === 'string' && (UI_LANGUAGES as readonly string[]).includes(tag);

/**
 * Collapse any BCP-47 tag onto a language the chrome ships: `es-MX` and
 * `es-419` are Spanish, `pt-BR` has no catalog yet so it reads English.
 */
export const resolveUiLanguage = (tag: string | null | undefined): UiLanguage => {
  if (!tag) return DEFAULT_UI_LANGUAGE;
  const base = tag.trim().toLowerCase().split(/[-_]/)[0];
  return isUiLanguage(base) ? base : DEFAULT_UI_LANGUAGE;
};

/** localStorage throws in private mode and in some in-portal browsers. */
export const readStoredLanguage = (): UiLanguage | null => {
  try {
    const raw = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isUiLanguage(raw) ? raw : null;
  } catch {
    return null;
  }
};

export const storeLanguage = (tag: string): void => {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, resolveUiLanguage(tag));
  } catch {
    /* no storage: the choice lasts for this page, and the profile still has it */
  }
};

/** Screen readers and `:lang()` rules need the document to say what it is. */
export const applyDocumentLanguage = (tag: string): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = resolveUiLanguage(tag);
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ns: [...NAMESPACES],
    defaultNS: 'common',
    fallbackNS: 'common',
    supportedLngs: [...UI_LANGUAGES],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    fallbackLng: DEFAULT_UI_LANGUAGE,
    returnNull: false,
    // Everything is bundled, so resolve synchronously — no render happens
    // against a half-loaded store.
    initAsync: false,
    interpolation: {
      // React escapes for us; double-escaping mangles names and apostrophes.
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // We write storage ourselves (guarded) in `setUiLanguage`.
      caches: [],
    },
    react: { useSuspense: false },
  });

i18n.on('languageChanged', applyDocumentLanguage);
applyDocumentLanguage(i18n.resolvedLanguage || i18n.language);

/** The language the chrome is actually rendering in. */
export const activeUiLanguage = (): UiLanguage =>
  resolveUiLanguage(i18n.resolvedLanguage || i18n.language);

/** Switch the chrome and remember the choice locally. */
export const setUiLanguage = async (tag: string): Promise<UiLanguage> => {
  const next = resolveUiLanguage(tag);
  storeLanguage(next);
  if (activeUiLanguage() !== next) await i18n.changeLanguage(next);
  return next;
};

export default i18n;
